import { useState, useEffect } from 'react';
import { getNetworkDetector, isOnlineNow } from '@shared/lib/network';

export interface NetworkStatus {
  isOnline: boolean;
  isReconnecting: boolean;
  lastOnlineTime: Date | null;
}

export const useNetworkStatus = (): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(isOnlineNow());
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [lastOnlineTime, setLastOnlineTime] = useState<Date | null>(
    isOnlineNow() ? new Date() : null
  );

  useEffect(() => {
    const unsubscribe = getNetworkDetector().subscribe((online) => {
      setIsOnline(online);
      setIsReconnecting(false);
      if (online) setLastOnlineTime(new Date());
    });
    return unsubscribe;
  }, []);

  // Detect reconnection attempts
  useEffect(() => {
    if (!isOnline) {
      const interval = setInterval(() => {
        // Try to fetch a small resource to test connectivity
        // Use welcome endpoint as requested and only mark online on OK
        fetch('https://api.hintword.com/welcome', {
          method: 'GET',
          cache: 'no-cache',
          mode: 'cors',
          credentials: 'omit',
          signal: AbortSignal.timeout(3000) // 3 second timeout
        })
        .then((res) => {
          if (res.ok && !isOnline) {
            setIsReconnecting(true);
            // Small delay to show reconnecting state
            setTimeout(() => {
              setIsOnline(true);
              setIsReconnecting(false);
              setLastOnlineTime(new Date());
            }, 1000);
          }
        })
        .catch(() => {
          setIsReconnecting(false);
        });
      }, 10000); // Check every 10 seconds to avoid too frequent requests

      return () => clearInterval(interval);
    }
  }, [isOnline]);

  return {
    isOnline,
    isReconnecting,
    lastOnlineTime,
  };
};
